import { useState } from "react";
import { Tag, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Input } from "@/components/ui/input";

interface MultiTagSelectorProps {
  availableTags: string[];
  selectedTags: string[];
  onTagsChange: (tags: string[]) => void;
  triggerButton?: React.ReactNode;
}

export default function MultiTagSelector({
  availableTags,
  selectedTags,
  onTagsChange,
  triggerButton,
}: MultiTagSelectorProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [tempSelectedTags, setTempSelectedTags] = useState<string[]>([]);

  const handleToggleTag = (tag: string) => {
    setTempSelectedTags(prev =>
      prev.includes(tag)
        ? prev.filter(t => t !== tag)
        : [...prev, tag]
    );
  };

  const handleSelectAll = () => {
    const filteredTags = availableTags.filter(tag =>
      tag.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setTempSelectedTags(filteredTags);
  };

  const handleClearAll = () => {
    setTempSelectedTags([]);
  };

  const handleApply = () => {
    onTagsChange(tempSelectedTags);
    setSearchTerm("");
    setIsOpen(false);
  };

  const handleCancel = () => {
    setTempSelectedTags(selectedTags);
    setSearchTerm("");
    setIsOpen(false);
  };

  const filteredTags = availableTags.filter(tag =>
    tag.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleOpenChange = (open: boolean) => {
    setIsOpen(open);
    if (open) {
      // Initialize when opening - this runs after parent has re-rendered
      setTempSelectedTags(selectedTags);
      setSearchTerm("");
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        {triggerButton || (
          <Button
            variant="outline"
            size="sm"
            data-testid="button-select-tags"
          >
            <Tag className="h-4 w-4 mr-2" />
            Select Tags
            {selectedTags.length > 0 && (
              <Badge variant="secondary" className="ml-2">
                {selectedTags.length}
              </Badge>
            )}
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="w-screen h-screen max-w-none max-h-none m-0 rounded-none overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Select Tags</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Search */}
          <Input
            placeholder="Search tags..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            data-testid="input-search-tags"
          />

          {/* Action Buttons */}
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={handleSelectAll}
              data-testid="button-select-all-tags"
            >
              Select All
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handleClearAll}
              data-testid="button-clear-all-tags"
            >
              Clear All
            </Button>
          </div>

          {/* Tags List */}
          <ScrollArea className="h-[300px] border rounded-md p-4">
            {filteredTags.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                {searchTerm ? "No tags match your search" : "No tags available"}
              </div>
            ) : (
              <div className="space-y-2">
                {filteredTags.map((tag) => (
                  <div
                    key={tag}
                    className="flex items-center space-x-2 p-2 hover:bg-accent rounded-md cursor-pointer"
                    onClick={() => handleToggleTag(tag)}
                  >
                    <Checkbox
                      checked={tempSelectedTags.includes(tag)}
                      onCheckedChange={() => handleToggleTag(tag)}
                      data-testid={`checkbox-tag-${tag}`}
                    />
                    <label className="flex-1 cursor-pointer text-sm">
                      {tag}
                    </label>
                    {tempSelectedTags.includes(tag) && (
                      <Check className="h-4 w-4 text-primary" />
                    )}
                  </div>
                ))}
              </div>
            )}
          </ScrollArea>

          {/* Selected Tags Preview */}
          {tempSelectedTags.length > 0 && (
            <div className="space-y-2">
              <div className="text-sm font-medium">
                Selected ({tempSelectedTags.length})
              </div>
              <div className="flex flex-wrap gap-2">
                {tempSelectedTags.map((tag) => (
                  <Badge
                    key={tag}
                    variant="secondary"
                    className="cursor-pointer"
                    onClick={() => handleToggleTag(tag)}
                  >
                    {tag}
                  </Badge>
                ))}
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button 
            variant="outline" 
            onClick={() => {
              handleCancel();
              setIsOpen(false);
            }} 
            data-testid="button-cancel-tags"
          >
            Cancel
          </Button>
          <Button onClick={handleApply} data-testid="button-apply-tags">
            Apply
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
